import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Github } from 'lucide-react';
import { authApi } from '../api/auth';
import { toast } from '../components/Toast';

export function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (searchParams.get('registered') === '1') {
      toast.info('注册成功，请登录');
    }
  }, [searchParams]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      await authApi.login({ email, password });
      navigate('/spaces');
    } catch (err) {
      setError(err instanceof Error ? err.message : '登录失败');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="flex min-h-screen">
      {/* 左侧品牌 */}
      <div className="hidden flex-1 flex-col justify-between bg-bg-surface p-12 md:flex">
        <Link to="/" className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-accent text-sm font-bold text-text-inverse shadow-sm">
            C
          </div>
          <span className="text-xl font-semibold tracking-tight">CoMind</span>
        </Link>
        <div className="space-y-6">
          <div className="h-32 w-48 rounded-2xl bg-accent/5 ring-1 ring-accent/10" />
          <div>
            <h2 className="text-3xl font-semibold tracking-tight text-[#1a1a2e]">小团队，大协作</h2>
            <p className="mt-2 text-text-secondary">文档与思维导图，一个工具搞定</p>
          </div>
        </div>
        <div className="text-sm text-text-tertiary">© 2026 CoMind</div>
      </div>

      {/* 右侧表单 */}
      <div className="flex flex-1 items-center justify-center bg-bg-canvas p-12">
        <div className="w-full max-w-sm">
          <div className="mb-8">
            <h1 className="text-2xl font-bold tracking-tight text-[#1a1a2e]">欢迎回来</h1>
            <p className="mt-1 text-sm text-text-secondary">登录你的账号继续使用</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="mb-1.5 block text-sm font-medium text-text-primary" htmlFor="email">
                邮箱
              </label>
              <input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input"
                placeholder="you@example.com"
              />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-text-primary" htmlFor="password">
                密码
              </label>
              <input
                id="password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input"
                placeholder="••••••••"
              />
              <div className="mt-1.5 flex justify-end">
                <Link to="/forgot-password" className="text-xs text-text-tertiary hover:text-accent transition-colors">
                  忘记密码？
                </Link>
              </div>
            </div>

            {error && (
              <div className="rounded-lg bg-danger/5 border border-danger/20 px-3 py-2 text-sm text-danger">
                {error}
              </div>
            )}

            <button type="submit" disabled={loading} className="btn btn-primary btn-md w-full shadow-sm">
              {loading ? '登录中...' : '登录'}
            </button>
          </form>

          <div className="my-6 flex items-center gap-3 text-xs text-text-tertiary">
            <div className="h-px flex-1 bg-border" />
            或使用
            <div className="h-px flex-1 bg-border" />
          </div>

          <div className="flex gap-3">
            <button type="button" className="btn btn-secondary btn-md flex-1">
              <Github className="h-4 w-4" /> GitHub
            </button>
            <button type="button" className="btn btn-secondary btn-md flex-1">
              Google
            </button>
          </div>

          <p className="mt-6 text-center text-sm text-text-secondary">
            还没账号？{' '}
            <Link to="/register" className="text-accent hover:underline font-medium">
              立即注册
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}